/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSpring, animated } from 'react-spring';
import { FormHelpers } from 'ontraccr-common';

import FormSectionHeader from './FormSectionHeader';
import FormFieldList from '../FormFields/FormFieldList';
import DraggableWrapper from '../DraggableWrapper';

import colors from '../../../constants/Colors';

const COLLAPSED_HEIGHT = 40;
const EXPANDED_HEIGHT = 600;

const border = '1px solid lightgray';

export default function FormSection({
  id,
  projectId,
  costcodes,
  costcodeIdMap,
  costcodesLoading,
  title,
  index,
  settings,
  dragging,
  onDelete,
  onRename,
  onSectionsSettingUpdated,
  onFieldAdd,
  onFieldDelete,
  fields = [],
  isDisplay = false,
  isAdd = false,
  sections = [],
  isBoardCards,
  isExternalForm,
  isMultiDivisionForm,
  divisions,
  templateId,
  name,
  shouldHideCustomRenderingFields,
  enablePermissions,
  isDetailView,
  isTimeCard,
  canAddDynamicAttribute,
  showParentAttributeCheckbox,
  isDragDisabled = false,
  allowRepeatingSections = false,
  isV3Form = false,
  shouldRespectCollapse = false,
}) {
  const [open, setOpen] = useState(() => (
    shouldRespectCollapse ? !settings?.defaultCollapsed : true
  ));
  const [style, setStyle] = useSpring(() => ({
    maxHeight: (shouldRespectCollapse && settings?.defaultCollapsed)
      ? COLLAPSED_HEIGHT
      : EXPANDED_HEIGHT,
  }));
  const onClick = useCallback(() => {
    setOpen(!open);
  }, [open]);
  const onDeleteClicked = useCallback(() => {
    onDelete(id);
  }, [onDelete, id]);

  const fieldDelete = useCallback((fieldId) => () => {
    onFieldDelete({
      fieldId,
      sectionId: id,
    });
  }, [id, onFieldDelete]);

  useEffect(() => {
    setStyle({
      maxHeight: open ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
    });
  }, [open]);

  const responses = useMemo(() => {
    const responseMap = {};
    sections.forEach(({ fields = [] }) => {
      fields.forEach(({ id, response }) => {
        responseMap[id] = response;
      });
    });

    return responseMap;
  }, [sections]);

  const nonscrollableMaxHeight = open ? undefined : COLLAPSED_HEIGHT;

  const {
    hasConditionalRendering: sectionHasConditionalRendering = false,
    conditionalRenderingFormula: sectionConditionalRenderingFormula,
  } = settings || {};

  if (
    shouldHideCustomRenderingFields
    && sectionHasConditionalRendering
    && FormHelpers.isConditionalRenderingFormulaComplete(sectionConditionalRenderingFormula)
    && !FormHelpers.executeConditionalRenderingFormula({
      formula: sectionConditionalRenderingFormula,
      responses,
    })
  ) {
    return null;
  }

  return (
    <DraggableWrapper
      isDragDisabled={isDragDisabled}
      draggableId={id}
      index={index}
      type="SECTION"
      isDisabled={isDisplay}
    >
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <animated.div
          {...draggableProps}
          style={{
            border: isDetailView ? 'none' : border,
            marginBottom: 5,
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: 'white',
            ...style,
            ...draggableProps?.style,
            boxShadow: dragging ? '0px 2px 8px rgba(75, 75, 75, 0.25)' : null,
            maxHeight: nonscrollableMaxHeight,
          }}
          ref={innerRef}
        >
          <div {...dragHandleProps}>
            <FormSectionHeader
              id={id}
              costcodes={costcodes}
              title={title}
              style={{
                // Subtract 1 for border or end of animation has a weird snap effect
                minHeight: COLLAPSED_HEIGHT - 1,
                borderBottom: isDetailView ? 'none' : border,
              }}
              open={open}
              onClick={onClick}
              onDeleteClicked={onDeleteClicked}
              onRename={onRename}
              isDisplay={isDisplay}
              settings={settings}
              onSectionsSettingUpdated={onSectionsSettingUpdated}
              enablePermissions={enablePermissions}
              isBoardCards={isBoardCards}
              isExternalForm={isExternalForm}
              isMultiDivisionForm={isMultiDivisionForm}
              divisions={divisions}
              sections={sections}
              isTimeCard={isTimeCard}
              allowRepeatingSections={allowRepeatingSections}
            />
          </div>
          { open && (
            <animated.div
              style={{
                height: style.maxHeight.interpolate({
                  range: [COLLAPSED_HEIGHT, EXPANDED_HEIGHT],
                  output: [0, 'fit-content'],
                }),
                maxHeight: nonscrollableMaxHeight,
                backgroundColor: isDetailView ? 'white' : colors.OPACITY_LIGHT_GRAY,
                paddingBottom: 0,
                overflow: 'hidden',
              }}
            >
              <FormFieldList
                id={id}
                projectId={projectId}
                costcodes={costcodes}
                costcodeIdMap={costcodeIdMap}
                costcodesLoading={costcodesLoading}
                fields={fields}
                onSave={onFieldAdd}
                onDelete={fieldDelete}
                isDisplay={isDisplay}
                isAdd={isAdd}
                sections={sections}
                isBoardCards={isBoardCards}
                isExternalForm={isExternalForm}
                isMultiDivisionForm={isMultiDivisionForm}
                divisions={divisions}
                templateId={templateId}
                name={name}
                shouldHideCustomRenderingFields={shouldHideCustomRenderingFields}
                responses={responses}
                isDetailView={isDetailView}
                canAddDynamicAttribute={canAddDynamicAttribute}
                showParentAttributeCheckbox={showParentAttributeCheckbox}
                isTimeCard={isTimeCard}
                isDragDisabled={isDragDisabled}
                allowRepeatingSections={allowRepeatingSections}
                isV3Form={isV3Form}
              />
            </animated.div>
          )}
        </animated.div>
      )}
    </DraggableWrapper>
  );
}
